'use client';
import React, { PropsWithChildren, useContext } from 'react';
import { CME, CMECredit } from '@osmosis/shared/types/learn';

import { useQuery } from 'react-query';
import { useFetch } from './FetchProvider';
import { getCookie, setCookie } from 'cookies-next';
import useModal from '../hooks/useModal';
import Snackbar from '../components/Snackbar';
import { useUserContext } from './UserContext';

export enum CMEType {
  VIDEO = 'video',
  QUIZ = 'quiz',
}

export type CMEContextProps = {
  updateCME: (type: CMEType) => Promise<CME>;
  handleCreditStatus: (data?: { cases: { completed: boolean } }) => void;
  cme?: CME;
  videoCredit?: CMECredit;
  quizCredit?: CMECredit;
  concept?: string;
  type: CMEType;
  cookieName: string;
  cmeModal: ReturnType<typeof useModal>;
};

const CMEContext = React.createContext<CMEContextProps>({} as CMEContextProps);

/**
 * CME provider.
 */
export const CMEProvider = (
  props: PropsWithChildren<{
    cme?: CME;
    concept?: string;
    learnNodeId?: number;
    type: CMEType;
    cookieName: string;
  }>,
) => {
  const { children, concept, learnNodeId, type, cookieName } = props;

  const { user } = useUserContext();
  const { put, get } = useFetch();

  const cmeModal = useModal();

  const { data: cme, refetch } = useQuery(
    'cmeData',
    async () => {
      if (!learnNodeId) {
        return Promise.resolve(null);
      }
      const { data } = await get<CME>(`/v0/cme_data`, {
        params: { learnNodeId },
      });

      return data;
    },
    { refetchOnWindowFocus: false, enabled: !!user },
  );

  const getCreditByType = (type: CMEType) => {
    if (!cme?.enabled) {
      return null;
    }

    return cme?.credits?.find((c) => c.type === type) || null;
  };

  const cmeSnackbar = useModal();

  const updateCME = async (type: CMEType) => {
    if (concept) {
      const { data } = await put(
        `/v0/cme_credit/${encodeURIComponent(concept)}`,
        {
          type,
        },
      );

      await refetch();

      return data;
    }

    return null;
  };

  const handleCreditStatus = (
    args: { cases?: { completed?: boolean } } = {},
  ): void => {
    const credit = getCreditByType(type);

    if (!credit) return;

    switch (credit.status) {
      case 'unregistered':
        void updateCME(CMEType.VIDEO);

        return;
      case 'registered':
        setCookie(cookieName, false);

        return;
      case 'completed':
        if ((args?.cases?.completed ?? true) && !getCookie(cookieName)) {
          cmeSnackbar.show();
          setCookie(cookieName, true);
        }

        return;
      default:
        return;
    }
  };

  return (
    <CMEContext.Provider
      value={
        {
          cme,
          videoCredit: getCreditByType(CMEType.VIDEO),
          quizCredit: getCreditByType(CMEType.QUIZ),
          updateCME,
          concept,
          handleCreditStatus,
          cmeModal,
        } as CMEContextProps
      }
    >
      <Snackbar
        isVisible={cmeSnackbar.isVisible}
        hide={cmeSnackbar.hide}
        message={`You’ve earned ${cme?.credits?.[0]?.credits} CME credits!`}
      />

      {children}
    </CMEContext.Provider>
  );
};

/**
 * Hook to access the user context.
 */
export const useCMEContext = (): CMEContextProps => useContext(CMEContext);
